import { useTranslation } from 'react-i18next';
import { usePhysicianForm } from '../hooks/usePhysicianForm';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function PhysicianForm({ person }) {
    const { t } = useTranslation();
    const {
        physicianFormRegister,
        physicianErrors,
        physicianData,
        handleEdit,
        handleDelete,
        isEditing,
        handleSubmit,
        submitPhysicianForm,
        handleCancel,
        watch,
        setValue,
        errors
    } = usePhysicianForm(person);

    // Helper function to format dates
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    const onPhysicianSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(submitPhysicianForm)(e);
    };

    return (
        <div className="physician-form-container">
            <h3 className="form-subtitle">
                {t('persons.physician.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>
            <div className="form-content">
                <div className="form-section">
                    <form onSubmit={onPhysicianSubmit}>
                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.physician.physicianName')}</span>
                            <div className='form-input-fields'>
                                <div className="physician-name-inputs">
                                    <input
                                        type="text"
                                        name="physicianFirstName"
                                        placeholder={t('persons.physician.firstName')}
                                        className={`form-control ${physicianErrors.physicianFirstName ? 'is-invalid' : ''}`}
                                        {...physicianFormRegister('physicianFirstName')}
                                    />
                                    <input
                                        type="text"
                                        name="physicianMiddleName"
                                        placeholder={t('persons.physician.middleName')}
                                        className={`form-control ${physicianErrors.physicianMiddleName ? 'is-invalid' : ''}`}
                                        {...physicianFormRegister('physicianMiddleName')}
                                    />
                                    <input
                                        type="text"
                                        name="physicianLastName"
                                        placeholder={t('persons.physician.lastName')}
                                        className={`form-control ${physicianErrors.physicianLastName ? 'is-invalid' : ''}`}
                                        {...physicianFormRegister('physicianLastName')}
                                    />
                                    <input
                                        type="hidden"
                                        {...physicianFormRegister('physicianName')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.physician.inForceFrom')} <span className="required">*</span></span>
                            <div className='form-input-fields'>
                                <CustomDatePicker
                                    name="inForceFrom"
                                    value={watch('inForceFrom')}
                                    onChange={(date) => setValue('inForceFrom', date, { shouldValidate: true })}
                                    errors={physicianErrors}
                                    placeholderText={t('common.dateFormat')}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.physician.addressOfPractice')}</span>
                            <div className='form-input-fields'>
                                <input
                                    type="text"
                                    name="addressOfPractice"
                                    placeholder={t('persons.physician.addressOfPractice')}
                                    className={`form-control ${physicianErrors.addressOfPractice ? 'is-invalid' : ''}`}
                                    {...physicianFormRegister('addressOfPractice')}
                                />
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.physician.physicianPhone')}</span>
                            <div className='form-input-fields'>
                                <input
                                    type="text"
                                    name="physicianPhone"
                                    placeholder={t('persons.physician.physicianPhone')}
                                    className={`form-control ${physicianErrors.physicianPhone ? 'is-invalid' : ''}`}
                                    {...physicianFormRegister('physicianPhone')}
                                />
                            </div>
                        </div>

                        <div className="physician-form-controls">
                            <button
                                type="button"
                                className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                                onClick={handleSubmit(submitPhysicianForm)}
                            >
                                {isEditing ? t('common.edit') : t('common.add')}
                            </button>
                            {isEditing && (
                                <button
                                    type="button"
                                    className="form-btn btn-gray"
                                    onClick={handleCancel}
                                >
                                    {t('common.cancel')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="table-section">
                    <div className="physician-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('persons.physician.inForceFrom')}</th>
                                    <th>{t('persons.physician.physicianName')}</th>
                                    <th>{t('persons.physician.addressOfPractice')}</th>
                                    <th>{t('persons.physician.physicianPhone')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {physicianData?.map((physician) => (
                                    <tr key={physician.id}>
                                        <td>{formatDate(physician.effectiveDate)}</td>
                                        <td>{physician.jpName}</td>
                                        <td>{physician.jpAddress}</td>
                                        <td>{physician.jpPhone}</td>
                                        <td className="action-buttons">
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="action-icon edit"
                                                onClick={() => handleEdit(physician)}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="action-icon delete"
                                                onClick={() => handleDelete(physician.id)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
} 